import { Injectable } from '@angular/core';
import { TranslateService } from '../../utilities/translate/translate.service'; // Translation support.
import * as moment from 'moment';
import { MatrixHeader, MatrixBody } from './matrix.interface';

/**
 * Matrix Service is used to generate the CSV
 *
 * This service is basically created to parse the matrix.controller data.
 */

@Injectable({
  providedIn: 'root'
})
export class MatrixService {
  // Some predefined variables which are usefull for properly setting the csv.
  private readonly  separator: string = ';';
  private readonly  newLine: string = '\n';
  private readonly  uni: string = '\uFEFF';
  private body: MatrixBody;
  private header: MatrixHeader;
  private filename: string;

  constructor(
    private translateService: TranslateService
  ) {}

  public prepare(
    headers: HTMLElement,
    collection: HTMLElement,
    name: string) {

    this.header = this._createHeader(headers);
    this.body = this._createBody(collection);
    this.filename = this._createFileName(name);
    this._export();
  }

  // Let's create an array of all the modules, submodules and rendered element titles.
  private _createHeader = ( headers: any) => {
    const collection = Array.from(headers);
    const model: any = [];

    collection.forEach((item: any) => {

      const modules = Array.from(item.getElementsByClassName('matrix-table__module-title'));
      const elements: any = Array.from(item.getElementsByClassName('v-text'));

      if (modules.length >= 1) {
        modules.forEach((m: any) => {
          model.push({
            title: m.title,
            elements: [] // elements will be added here.
          });
        });
      }

      for (const idx in elements) {
        if ( elements[idx] ) {
          model.filter(m => {
            if (m.title === elements[idx].title) {
              // Each element should be added to the array.
              m.elements.push({
                value: elements[idx].dataset.value,
                unit: elements[idx].dataset.unit,
              });
            }
          });
        }
      }

    });

    return model;
  }

  /**
   * Generate a model which contains an array of areas with their own elements.
   *
   */
  private _createBody = ( body: any) => {
    const { rows } = body;

    if (!rows.length) {
      return;
    }

    const model: any = [];

    for (const i in rows) {
      if (rows[i].cells) {
        for (const c in rows[i].cells) {
          if (!rows[i].cells[0].classList.contains('matrix-table__cluster-header')) {
            // Instead of fetching these data throughout our model, why not use the already defined data, by using datasets?
            if (typeof rows[i].cells[c].dataset !== 'undefined') {

              // Get the area name & fno
              if (rows[i].cells[c].classList.contains('area-column')) {
                const { areaName, areaFno, areaId } = rows[i].cells[c].dataset;
                model.push({areaId, areaName, areaFno, elements: []});
              } else {
                const cells = Array.from(rows[i].cells[c].getElementsByClassName('v-text'));

                // We need to loop through the cells and check if a dataset is defined.
                // If not we create an "empty cell".
                // Empty cells are neded to make sure that the export loads the values in it's correct column.
                cells.forEach((cell: any) => {
                  const dataCollection: any = Array.from(cell.getElementsByClassName('selected-element-set'));
                  const areaId = cell.dataset.area;

                  model.filter(m => {
                    if (m.areaId === areaId) {
                      m.elements.push(Object.assign({}, dataCollection[0].dataset));
                    }
                    return m;
                  });
                });
              }

            }

          }

        }

      }
    }

    return model;
  }


  private _convertHeaderToCSV() {
    if (!this.header) {
      return;
    }

    // Shift 1 cell
    let modules = this.separator;
    let elements = this.newLine + this.separator;

    modules += this.separator;

    for ( const i in this.header) {
      if (this.header[i]) {
        modules += this.header[i].title;
        this.header[i].elements.forEach(element => {
          modules += this.separator;
        });
      }
    }

    elements += this.newLine;
    elements += this.separator + this.translateService.get('areasurface');
    elements += this.separator;

    for ( const i in this.header) {
      if (this.header[i]) {
        this.header[i].elements.forEach(element => {
          const unit = element.unit === 'none' ? '' : element.unit;
          elements += `${element.value} ${unit}`;
          elements += this.separator;
        });
      }
    }

    return modules + elements;
  }

  private _convertBodyToCSV() {
    if (!this.body) {
      return;
    }
    let row = '';
    row += this.newLine;

    for ( const i in this.body) {
      if ( i ) {
        row += this.body[i].areaName;
        row += this.separator;
        row += this.body[i].areaFno;

        this.body[i].elements.forEach(element => {
          row += this.separator;
          if (element.value !== 'EMPTY_CELL') {
            row += `${element.value}`;
          } else {
            row += '';
          }
        });
        row += this.newLine;
      }
    }

    return row;
  }


  // Prepare the filename that will be used when creating the csv file
  private _createFileName (name: string) {
    return `matrix_export_${name.toLowerCase().replace(' ', '')}_${moment().unix()}.csv`;
  }

  /**
   * Export to csv
   *
   * Combinding the header/body to a csv text file which can
   */
  private _export() {
    let data;
    let csv = this._convertHeaderToCSV() + this._convertBodyToCSV();

    if (csv === null) {
      return;
    }

    csv = csv.replace(/;,/g, ';');

    if (!csv.match(/^data:text\/csv/i)) {
      csv = 'data:text/csv;charset=utf-8,' + this.uni + csv;
    }

    data = encodeURI(csv);
    this._download(data);
  }

  private _download(d) {
    const link = document.createElement('a');
    link.setAttribute('href', d);
    link.setAttribute('download', this.filename);
    link.click();
  }
}
